import React, { useMemo } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  VehicleOption,
  useVehicleOptions,
  vehicleOptionHaystack,
  vehicleOptionLabel,
} from "../../api/vehicleOptions";

interface Props {
  fleetId: number;
  /** Selected vehicle id, or null. Controlled — the parent owns the value. */
  value: number | null;
  onChange: (vehicleId: number | null) => void;
  label?: string;
  placeholder?: string;
  /** Presents the empty selection as an explicit "all vehicles" filter state. */
  allowAll?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}

/**
 * Searchable vehicle dropdown.
 *
 * Replaces the free-text `vehicle_id` fields on the cost and fiscal-document
 * forms. Three properties matter and are worth stating, because each one is a
 * decision rather than a default:
 *
 *  - It is NOT `freeSolo`. The TCO search is, because there a stray string
 *    merely fails to match. Here the value is written to the ledger, so the
 *    component must only ever emit an id that came from the server.
 *  - `filterOptions` is capped. A thousand mounted list items make the first
 *    keystroke visibly slow, and nobody scrolls a thousand plates anyway — the
 *    cap is what keeps the field instant on a large fleet.
 *  - It matches on VIN and id as well as plate, so the people who do know the
 *    id (support, anyone pasting from a spreadsheet) keep their shortcut
 *    instead of losing it to the new UI.
 */
export const VehiclePicker: React.FC<Props> = ({
  fleetId,
  value,
  onChange,
  label,
  placeholder,
  allowAll = false,
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = "small",
  sx,
}) => {
  const { t } = useTranslation();
  const { options, byId, truncated, isLoading } = useVehicleOptions(fleetId);

  const selected =
    value === null || value === undefined ? null : byId.get(value) ?? null;

  const filterOptions = useMemo(
    () =>
      createFilterOptions<VehicleOption>({
        limit: 50,
        stringify: vehicleOptionHaystack,
        trim: true,
      }),
    []
  );

  // i18next v23 types `t()` as a ReactNode-ish union, which makes TextField's
  // discriminated prop union fail to resolve once `params` is spread in.
  // These fields are plain strings; saying so keeps the inference simple.
  const fieldLabel = String(label ?? t("vehiclePicker.label", "Vehicule"));

  const fieldPlaceholder = String(
    placeholder ??
      (allowAll
        ? t("vehiclePicker.placeholderAll", "Tous les vehicules")
        : t("vehiclePicker.placeholder", "Rechercher une plaque, une marque..."))
  );

  const hint =
    helperText ??
    (truncated
      ? String(
          t(
            "vehiclePicker.truncated",
            "Liste limitee : affinez votre recherche pour trouver un vehicule absent."
          )
        )
      : undefined);

  return (
    <Autocomplete<VehicleOption, false, false, false>
      options={options}
      value={selected}
      loading={isLoading}
      disabled={disabled}
      size={size}
      sx={sx}
      // Opening on focus is what makes this read as a dropdown rather than a
      // text field that happens to suggest things.
      openOnFocus
      autoHighlight
      selectOnFocus
      handleHomeEndKeys
      clearOnBlur
      filterOptions={filterOptions}
      getOptionLabel={vehicleOptionLabel}
      isOptionEqualToValue={(option, current) =>
        option.vehicle_id === current.vehicle_id
      }
      onChange={(_event, option) => onChange(option?.vehicle_id ?? null)}
      noOptionsText={
        isLoading
          ? String(t("common.loading", "Chargement..."))
          : String(t("vehiclePicker.noMatch", "Aucun vehicule correspondant"))
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.vehicle_id}>
          <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {option.plate ?? `#${option.vehicle_id}`}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[option.brand, option.model].filter(Boolean).join(" ") ||
                t("common.notAvailable", "N/A")}
            </Typography>
          </Box>
          {option.status && (
            <Chip size="small" variant="outlined" label={option.status} sx={{ ml: 1 }} />
          )}
        </Box>
      )}
      // `params: any` mirrors CustomSearch: react-i18next augments JSX children
      // globally, which breaks TextField's discriminated prop union the moment
      // Autocomplete's InputLabelProps are spread in. The alternative is a cast
      // per prop, which reads worse for no extra safety.
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={fieldLabel}
          placeholder={fieldPlaceholder}
          required={required}
          error={error}
          helperText={hint}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="disabled" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default VehiclePicker;
